import { useState, useEffect } from 'react';
import { getSupabase } from '@/hooks/useSupabase';

export interface SiteSettings {
  site_name: string;
  site_description: string;
  site_keywords: string;
  site_url: string;
  logo_text: string;
  logo_url: string;
  favicon_url: string;
  og_image_url: string;
  meta_title: string;
  meta_description: string;
  meta_keywords: string;
  facebook_url: string;
  twitter_url: string;
  linkedin_url: string;
  instagram_url: string;
  contact_email: string;
  contact_phone: string;
  contact_address: string;
  [key: string]: string;
}

export function useSiteSettings() {
  const [settings, setSettings] = useState<SiteSettings>();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const supabase = getSupabase();
        const { data, error } = await supabase
          .from('site_settings')
          .select('key, value');

        if (error || !data) {
          setLoading(false);
          return;
        }

        const merged: Record<string, string> = {};
        data.forEach((row: { key: string; value: string }) => {
          if (row.value) {
            merged[row.key] = row.value;
          }
        });
        setSettings(merged as SiteSettings);
      } catch { /* keep undefined state */
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  // Apply document-level changes
  useEffect(() => {
    if (loading || !settings) return;

    // Title
    document.title = settings.meta_title || settings.site_name;

    // Meta description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', settings.meta_description || settings.site_description);

    // Meta keywords
    let metaKey = document.querySelector('meta[name="keywords"]');
    if (!metaKey) {
      metaKey = document.createElement('meta');
      metaKey.setAttribute('name', 'keywords');
      document.head.appendChild(metaKey);
    }
    metaKey.setAttribute('content', settings.meta_keywords || settings.site_keywords);

    // Favicon
    if (settings.favicon_url) {
      let favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
      if (!favicon) {
        favicon = document.createElement('link');
        favicon.rel = 'icon';
        document.head.appendChild(favicon);
      }
      favicon.href = settings.favicon_url;
    }

    // OG tags
    const setOgTag = (property: string, content: string) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };

    setOgTag('og:title', settings.meta_title || settings.site_name);
    setOgTag('og:description', settings.meta_description || settings.site_description);
    if (settings.og_image_url) setOgTag('og:image', settings.og_image_url);
    setOgTag('og:url', settings.site_url);

    // Twitter
    const setTwitterTag = (name: string, content: string) => {
      let tag = document.querySelector(`meta[name="${name}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('name', name);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };
    setTwitterTag('twitter:title', settings.meta_title || settings.site_name);
    setTwitterTag('twitter:description', settings.meta_description || settings.site_description);
    if (settings.og_image_url) setTwitterTag('twitter:image', settings.og_image_url);
  }, [settings, loading]);

  return { settings, loading };
}